import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/electron/renderer'

import { fetchChatMessages } from '@renderer/chat/hooks/chat-messages'
import { isActionRunInItsChat } from '@renderer/chat/integrations/conversation-action'
import { useAuth } from '@renderer/chat/providers'
import { useIsTurnLive } from '@renderer/chat/store'
import type { SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { useForYouChatsFeed, useSidepanelChatRow } from '@renderer/overlay/hooks/use-running-chats'
import { FOR_YOU_CHATS_QUERY_KEY } from '@renderer/stores/conversation-query-keys'
import { STAGED_ACTIONS_QUERY_KEY, useVisibleStagedActions } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { lastAssistantText, orderStagedActionsChronologically } from './chat-output'

export interface ChatOutputViewContext {
  conversationId: string
  title?: string
  conversationType?: string
  skillId?: string
  preview?: string
  /** The chat row's current drafts; the targets a follow-up edit request attaches to. */
  stagedActionIds?: Array<string>
  reminderIds?: Array<string>
  audioNoteId?: string
  previewRevision?: number
  chat?: boolean
  focusedActionId?: string
  /** A local submit owns the sheet's live stream until it settles. */
  isFreshSubmit?: boolean
}

/** The context a For You row opens with; the row's action ids survive the feed dropping it. */
export function chatOutputContextOf(chat: SidepanelChat): ChatOutputViewContext {
  return {
    conversationId: chat.id,
    title: chat.title,
    conversationType: chat.conversationType,
    skillId: chat.skillId,
    preview: chat.preview,
    stagedActionIds: chat.stagedActionIds,
    reminderIds: chat.reminderIds,
    audioNoteId: chat.audioNoteId,
    previewRevision: chat.previewRevision,
  }
}

export const CHAT_OUTPUT_MESSAGES_QUERY_KEY = 'chat-output-messages'

/**
 * Whether a turn is in flight for the conversation. Shared by the view body and
 * the floating bar so their busy states can't disagree.
 *
 * List row is the gateway-backed source of truth; the local turn state covers
 * the window before the running-chats refetch lands.
 */
export function useIsChatOutputStreaming(conversationId: string | undefined): boolean {
  const { data: chats } = useForYouChatsFeed()
  const isListStreaming = chats.some((row) => row.id === conversationId && row.isStreaming === true)
  const isLocalStreaming = useIsTurnLive(conversationId)
  return isListStreaming || isLocalStreaming
}

/** Latest completed assistant message and staged actions for a conversation. */
export function useChatOutputViewModel(context: ChatOutputViewContext | undefined) {
  const conversationId = context?.conversationId
  const queryClient = useQueryClient()
  const { getAuthToken } = useAuth()
  const { data: chats, isLoading: isFeedLoading } = useForYouChatsFeed()
  const feedRow = useMemo(() => chats.find((row) => row.id === conversationId), [chats, conversationId])
  // A settled run with no open action has left the feed, typically the other side of a compare pair; its row still has the title and preview.
  const fetchedRow = useSidepanelChatRow(conversationId, !feedRow && !isFeedLoading)
  const chat = feedRow ?? fetchedRow
  const preview = chat?.preview ?? context?.preview
  const previewMessages = useMemo(() => (preview ? [{ role: 'assistant', content: preview }] : undefined), [preview])

  const isStreaming = useIsChatOutputStreaming(conversationId)

  // The For You feed drops a conversation as soon as its last action settles, and
  // the row is where the ids come from — without this the cards would disappear
  // from under a user who is still reading the thread. Hold the last non-empty set
  // we saw for the OPEN conversation; opening another one starts over.
  const lastIdsRef = useRef<{ conversationId: string; ids: Array<string> } | undefined>(undefined)
  const { allActions, isLoading: isLoadingAllActions } = useVisibleStagedActions()
  const conversationActionIds = useMemo(() => {
    if (!conversationId) {
      return []
    }
    // A fetched row carries no action ids; the actions list still knows which conversation each card came from.
    const rowIds = chat?.stagedActionIds ?? []
    const ids =
      rowIds.length > 0
        ? rowIds
        : allActions.flatMap((action) => (action.conversationId === conversationId ? [action.id] : []))
    if (ids.length > 0) {
      lastIdsRef.current = { conversationId, ids }
      return ids
    }
    // A popout window opens before its feed and actions load; the ids the row opened with stand in.
    return lastIdsRef.current?.conversationId === conversationId
      ? lastIdsRef.current.ids
      : (context?.stagedActionIds ?? [])
  }, [allActions, chat?.stagedActionIds, context?.stagedActionIds, conversationId])

  const actionsById = useMemo(() => new Map(allActions.map((action) => [action.id, action])), [allActions])

  // An action the planner runs in this chat would offer, as a card here, to start what already started.
  const stagedActionIds = useMemo(
    () =>
      conversationActionIds.filter((id) => {
        const action = actionsById.get(id)
        return !action || !isActionRunInItsChat(action)
      }),
    [actionsById, conversationActionIds],
  )

  const stagedActions = useMemo(
    () =>
      orderStagedActionsChronologically(
        stagedActionIds.flatMap((id) => {
          const action = actionsById.get(id)
          return action ? [action] : []
        }),
      ),
    [actionsById, stagedActionIds],
  )

  const messagesQuery = useQuery({
    queryKey: [CHAT_OUTPUT_MESSAGES_QUERY_KEY, conversationId],
    queryFn: async () => {
      if (!conversationId) {
        return []
      }
      return fetchChatMessages(conversationId, getAuthToken)
    },
    enabled: !!conversationId && !isStreaming,
    placeholderData: previewMessages,
    staleTime: 30_000,
  })

  // When streaming ends, refresh the completed turn's inputs so the view
  // doesn't sit on a stale preview / message snapshot.
  const wasStreamingRef = useRef(isStreaming)
  useEffect(() => {
    const wasStreaming = wasStreamingRef.current
    wasStreamingRef.current = isStreaming
    if (!conversationId || !wasStreaming || isStreaming) {
      return
    }

    void Promise.all([
      queryClient.invalidateQueries({ queryKey: [CHAT_OUTPUT_MESSAGES_QUERY_KEY, conversationId] }),
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }),
    ]).catch((error) => Sentry.captureException(error))
  }, [conversationId, isStreaming, queryClient])

  const assistantText = useMemo(() => {
    return lastAssistantText(messagesQuery.data) ?? preview
  }, [messagesQuery.data, preview])

  return {
    title: chat?.title ?? context?.title ?? 'Untitled chat',
    conversationType: context?.conversationType ?? chat?.conversationType,
    skillId: context?.skillId ?? chat?.skillId,
    audioNoteId: context?.audioNoteId ?? chat?.audioNoteId,
    isStreaming,
    stagedActionIds,
    /** Some of the chat's ids name actions it runs itself, so an empty `stagedActionIds` is real, not unloaded. */
    hidesActionsRunInChat: stagedActionIds.length < conversationActionIds.length,
    stagedActions,
    // A fresh window has ids before the actions list lands; the summary would flash in the gap.
    isLoadingActions: isLoadingAllActions && stagedActionIds.length > 0,
    assistantText,
    isLoadingMessages: !isStreaming && messagesQuery.isLoading,
    isMessagesError: !isStreaming && messagesQuery.isError,
    chat,
  }
}
